import parse from 'html-react-parser';
import './Onboarding.css';
import { useCallback, useEffect, useState } from 'react';
import PlaceHolder from '../components/PlaceHolder';
import { AppIdentity, errorMessages, initialFormValues } from '../utilities/constants';
import Dialog from '../components/Dialog';
import OnboardingForm from '../components/OnboardingForm';
import {
  fetchPricingDetails,
  getAppIdandSPNData,
  getAppList,
  getModelsForOnboardingApps
} from '../service/apiServices';
import { useAzureData } from '../context/AzureContext';
import PricingModel from '../components/PricingModel';
import { CircleChevronLeft } from 'lucide-react';
import add_app from '../assets/icon/add_app.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingBox from '../components/LoadingBox';

type SPNAppIdEnvironmentMap = {
  environment: string;
  service_principal_name: string;
  application_id: string;
};
type ModelDetails = {
  model_id: string;
  model_name: string;
  prod: string;
  'non-prod': string;
};
type OnboardingFormValue = {
  onboarding_id?: number;
  environment: string[];
  application_name: string;
  model_name: string[];
  model_id: string[];
  primary_owner: string;
  wl3_approver: string;
  application_description: string;
  secondary_owner: string;
  icc: string;
  cc: string;
  aif_rag_access: boolean;
  ai_inventory_id: string;
  onboardingstatus: string | null;
  spn_appid_env_map: SPNAppIdEnvironmentMap[] | [];
  adgroupforappaccess: string | string[];
  company_code?: string;
  resource_group?: string;
  approval_email?: string | null;
  approval_email_uploaded?: boolean;
  foundation_rai_integration?: string[];
};
type TierDescription = {
  pricing: string;
  includes: [];
  notes: [];
};

type PricingDetails = {
  tier: string;
  tierdescription: {
    tierDescription: TierDescription;
  };
};
const CACHE_KEY = 'spnOptionsCache';
const CACHE_TTL = 1000 * 60 * 60 * 24;

function Onboarding() {
  const location = useLocation();
  const editAppId = location.state?.editAppId;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEditableForm, setShowEditableForm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [onboardedApps, setOnboardedApps] = useState<OnboardingFormValue[] | []>([]);
  const [modelDetails, setModelDetails] = useState<ModelDetails[] | []>([]);
  const [spnOptions, setSPNOptions] = useState<AppIdentity[] | []>();
  const [prefilledData, setPrefilledData] = useState<OnboardingFormValue | {}>({});
  const [showPricingModel, setShowPricingModel] = useState(true);
  const { apiToken, isAdmin } = useAzureData();
  const [tierInfo, setTierInfo] = useState('');
  const [pricingDetails, setPricingDetails] = useState<PricingDetails[] | []>([]);
  const navigate = useNavigate();

  const getModels = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getModelsForOnboardingApps(apiToken);
      setModelDetails(response);
    } catch (error: any) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  }, [apiToken]);

  const loadData = async () => {
    // alert('I m in in load function');
    // const cached = localStorage.getItem(CACHE_KEY);
    // if (cached) {
    //   const parsed = JSON.parse(cached);
    //   if (Date.now() < parsed.expiry) {
    //     setSPNOptions(parsed.value);
    //     return;
    //   }
    // }
    try {
      setIsLoading(true);
      const data: AppIdentity[] = await getAppIdandSPNData(apiToken);
      // console.log(data, 'datadatadata');
      setSPNOptions(data);
      // localStorage.setItem(
      //   CACHE_KEY,
      //   JSON.stringify({ value: data, expiry: Date.now() + CACHE_TTL })
      // );
    } catch (error: any) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    setShowEditableForm(false);
    setShowPricingModel(true);
  };

  const handleEdit = (id: number) => {
    setShowEditableForm(true);
    const editableFormValue = onboardedApps.find((app) => app.onboarding_id === id);
    if (editableFormValue) setPrefilledData(editableFormValue);
  };

  useEffect(() => {
    // alert('calling load function');
    loadData();
  }, []);

  useEffect(() => {
    getOnboardedApps();
    getPricingDetails();
  }, [showEditableForm]);

  useEffect(() => {
    getModels();
  }, [getModels]);

  useEffect(() => {
    if (editAppId && onboardedApps.length > 0) {
      const editableFormValue = onboardedApps.find((app) => app.onboarding_id === editAppId);
      if (editableFormValue) {
        setPrefilledData(editableFormValue); // prefill the form
        setShowEditableForm(true); // show step 2
        setShowPricingModel(false); // skip step 1
      }
    }
  }, [editAppId, onboardedApps]);

  // console.log(prefilledData, 'ddzzzddd');

  const convertValueintoArray = (result: any) => {
    const updatedData = result.map((item: any) => ({
      ...item,
      environment: [item.environment],
      adgroupforappaccess: Array.isArray(item.adgroupforappaccess)
        ? item.adgroupforappaccess
        : item.adgroupforappaccess.split(',')
    }));
    setOnboardedApps(updatedData);
  };

  const getOnboardedApps = async () => {
    try {
      setIsLoading(true);
      const result = await getAppList(apiToken);
      convertValueintoArray(result.onboarding_data);
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPricingDetails = async () => {
    try {
      const response = await fetchPricingDetails(apiToken);
      setPricingDetails(response.pricing_model_details);
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (errorMessage) {
    if (errorMessage.message === 'UNAUTHORIZED') return <Dialog />;
    return <PlaceHolder message={parse(errorMessages.serverError)} />;
  }

  if (isLoading)
    return (
      <div
        style={{ height: '100vh' }}
        className="container-fluid d-flex justify-content-center align-items-center">
        {/* <h4 className="text-center">Loading...</h4> */}
        <LoadingBox />
      </div>
    );

  const renderprefilledForm = () => (
    <OnboardingForm
      formData={prefilledData}
      listOfModels={modelDetails}
      buttonLabal={'Update'}
      listofAppIdentities={spnOptions}
      unEditableFields={true}
      pricingDetails={pricingDetails}
      handleNavigation={() => setShowPricingModel((prevState) => !prevState)}
      //
      //
      // loadData={() => loadData()}
    />
  );

  const renderForm = () => (
    <OnboardingForm
      formData={initialFormValues(tierInfo)}
      tierInfo={tierInfo}
      listOfModels={modelDetails}
      buttonLabal={'Submit'}
      listofAppIdentities={spnOptions}
      unEditableFields={false}
      pricingDetails={pricingDetails}
      handleNavigation={() => setShowPricingModel((prevState) => !prevState)}
      //
      // loadData={() => loadData()}
    />
  );

  const handleTier = (tier: string) => {
    if (!tier) return; // ignore null/undefined
    setShowPricingModel(false);
    setTierInfo(tier);
  };

  const Header = ({ step }: { step: 1 | 2 }) => {
    const isEditMode = showEditableForm || !!editAppId;

    const handleBack = () => {
      if (step === 1) {
        // On subscription page, always go back to My Apps
        navigate('/myapps');
      } else if (isEditMode) {
        // On edit form, go back to My Apps
        navigate('/myapps');
      } else {
        // On new onboarding form, go back to subscription page
        handleBackClick();
      }
    };

    return (
      <div className="form-header">
        <a
          className="back-link"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBack();
          }}>
          <CircleChevronLeft size={16} color="#A9ACB4" strokeWidth={1.5} /> Back
        </a>

        <h3 className="form-title">
          <img src={add_app} alt="Add App Icon" className="icon" />
          App Onboarding
        </h3>

        <p className="form-description">
          Connect and configure your application to begin leveraging AI-powered capabilities within
          the AI Foundation platform.
        </p>

        <div className="breadcrumb-container">
          <span className={`step ${step === 1 ? 'active' : 'deactive'}`}>1</span>
          <span className={step === 1 ? 'active-step' : 'deactive-step'}>Plan Selection</span>
          <span className="breadcrumb-separator">{'>'}</span>
          <span className={`step ${step === 2 ? 'active' : 'deactive'}`}>2</span>
          <span className={step === 2 ? 'active-step' : 'deactive-step'}>Onboarding Form</span>
        </div>
      </div>
    );
  };

  return (
    <div className="onboarding-container">
      <Header step={showPricingModel ? 1 : 2} />

      <div className="row text-center">
        {showPricingModel && (
          <div className="pricing-model-container mt-2">
            <PricingModel handleTier={handleTier} />
          </div>
        )}
      </div>

      {!showPricingModel && (
        <>
          {!showEditableForm && renderForm()}
          {showEditableForm && renderprefilledForm()}
        </>
      )}
    </div>
  );
}

export default Onboarding;
